/**
 * confirm-provider-dialog.tsx — Dialog to confirm an unknown provider.
 *
 * Calls POST /admin/referrals/:id/confirm-provider.
 * On success: calls onSuccess().
 * On error: shows RFC 9457 detail; stays open.
 * While in-flight: both buttons disabled.
 */

import { useState, type JSX } from 'react';

export interface ConfirmProviderDialogProps {
  onConfirm: () => Promise<void>;
  onClose: () => void;
  onSuccess: () => void;
}

export function ConfirmProviderDialog({
  onConfirm,
  onClose,
  onSuccess,
}: ConfirmProviderDialogProps): JSX.Element {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm(): Promise<void> {
    setIsPending(true);
    setError(null);
    try {
      await onConfirm();
      setIsPending(false);
      onSuccess();
    } catch (err) {
      setIsPending(false);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred.');
    }
  }

  return (
    <div role="dialog" aria-modal="true" className="space-y-4">
      <p className="text-sm text-gray-700">
        Confirm this provider and trigger a CMS eligibility check for this referral.
      </p>

      {error && <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={isPending}
          className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void handleConfirm()}
          disabled={isPending}
          className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          Confirm & Check Eligibility
        </button>
      </div>
    </div>
  );
}
